/**
 * The controls, written down once.
 *
 * A help screen that lists the keys is only useful while it is true, and a help
 * screen maintained separately from the input code stops being true the first
 * time somebody moves a binding. So this list is the source: the keyboard
 * adapter builds its key map from it, and the help screen prints it. Change a
 * binding here and both follow.
 *
 * `code` values are `KeyboardEvent.code`, not `key` — a French keyboard's `KeyW`
 * is where a QWERTY player's W is, which is what matters for WASD.
 */
/** Movement, which is held rather than triggered. */
export const MOVEMENT_BINDINGS = [
    { codes: ['KeyW', 'ArrowUp'], label: 'W / ↑', does: 'Walk forward' },
    { codes: ['KeyS', 'ArrowDown'], label: 'S / ↓', does: 'Walk back' },
    { codes: ['KeyA', 'ArrowLeft'], label: 'A / ←', does: 'Step left' },
    { codes: ['KeyD', 'ArrowRight'], label: 'D / →', does: 'Step right' },
    { codes: ['ShiftLeft', 'ShiftRight'], label: 'Shift', does: 'Run (uses stamina)' },
    { codes: ['Space'], label: 'Space', does: 'Jump' }
];
/** Everything else, which fires once per press. */
export const ACTION_BINDINGS = [
    { action: 'attack', binding: { codes: ['KeyF'], label: 'F', does: 'Attack whatever is in reach' } },
    { action: 'interact', binding: { codes: ['KeyE'], label: 'E', does: 'Talk, trade, travel, or read a sign' } },
    { action: 'inventory', binding: { codes: ['KeyI'], label: 'I', does: 'Your bag and what you are wearing' } },
    { action: 'map', binding: { codes: ['KeyM'], label: 'M', does: 'Where you are' } },
    { action: 'chat', binding: { codes: ['Enter'], label: 'Enter', does: 'Say something to everyone in this town' } },
    { action: 'help', binding: { codes: ['KeyH', 'Slash'], label: 'H  or  ?', does: 'This help screen' } },
    { action: 'settings', binding: { codes: ['Escape'], label: 'Esc', does: 'Settings, and close whatever is open' } },
    { action: 'ability1', binding: { codes: ['Digit1'], label: '1', does: 'Use the first thing on your bar' } },
    { action: 'ability2', binding: { codes: ['Digit2'], label: '2', does: 'Use the second' } },
    { action: 'ability3', binding: { codes: ['Digit3'], label: '3', does: 'Use the third' } },
    { action: 'ability4', binding: { codes: ['Digit4'], label: '4', does: 'Use the fourth' } }
];
/** The mouse. Described rather than bound, because buttons are not keys. */
export const MOUSE_CONTROLS = [
    { label: 'Left click the ground', does: 'Walk there. Let go of the keys and the mouse can do all of it.' },
    { label: 'Left click a creature', does: 'Walk up to it and attack. Keep clicking to keep swinging.' },
    { label: 'Left click a person', does: 'Walk over and talk to them.' },
    { label: 'Hold right button, move', does: 'Turn the camera.' },
    { label: 'Scroll wheel', does: 'Pull the camera in or push it out.' }
];
/** Touch, for the phone build. */
export const TOUCH_CONTROLS = [
    { label: 'Left thumb', does: 'Drag the stick to walk. Push it further to run.' },
    { label: 'Right side, drag', does: 'Turn the camera.' },
    { label: '⚔ button', does: 'Attack whatever is in reach.' },
    { label: '✋ button', does: 'Talk, trade, travel, or read a sign.' },
    { label: 'Tap the ground', does: 'Walk there.' }
];
/** Builds the code → action map the keyboard adapter needs. */
export function actionKeyMap() {
    const map = {};
    for (const { action, binding } of ACTION_BINDINGS) {
        for (const code of binding.codes)
            map[code] = action;
    }
    return map;
}
//# sourceMappingURL=controls.js.map